import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import MobileStickyBar from '../components/common/MobileStickyBar';
import {
  BookOpen, CheckSquare, Users, Calendar, Wallet,
  ArrowRight, Star, Heart, Briefcase, Music, Cake
} from 'lucide-react';

const GUIDES = [
  {
    id: 'anniversaire-adulte',
    href: '/guide/anniversaire-adulte',
    emoji: '🎉',
    title: 'Anniversaire adulte',
    desc: 'Thèmes 2026, checklists, budget, planning, prestataires. Tout pour une soirée inoubliable.',
    image: 'https://images.unsplash.com/photo-1748551204321-845c277c1dba?w=600&q=80',
    accent: '#F4A7B2',
    badge: 'Guide complet',
    sections: ['12 thèmes tendance', 'Checklist 40 points', 'Tableau budget', 'Planning Jour J'],
  },
  {
    id: 'mariage',
    href: '/guide/mariage',
    emoji: '💍',
    title: 'Mariage',
    desc: 'Organisation complète de votre Jour J, prestataires, décoration, animations et planning.',
    image: 'https://images.unsplash.com/photo-1581720848209-9721f8fa30ff?w=600&q=80',
    accent: '#D4AF37',
    badge: 'Premium',
    sections: ['Photobooth & animations', 'Budget mariage', 'Planning cérémonie', 'Prestataires'],
  },
  {
    id: 'anniversaire-enfant',
    href: '/guide/anniversaire-enfant',
    emoji: '🎈',
    title: 'Anniversaire enfant',
    desc: 'Châteaux gonflables, animations, déco thématique. Tout pour une fête magique.',
    image: 'https://images.unsplash.com/photo-1765947378663-9e7ad34ca310?w=600&q=80',
    accent: '#5DBCBF',
    badge: 'Très populaire',
    sections: ['Thèmes par âge', 'Checklist parents', 'Animations sûres', 'Planning fête'],
  },
  {
    id: 'entreprise',
    href: '/guide/entreprise',
    emoji: '💼',
    title: 'Événement entreprise',
    desc: 'Cocktails, inaugurations, team building, soirées CE. Organisation professionnelle.',
    image: 'https://images.unsplash.com/photo-1768508948485-a7adc1f3427f?w=600&q=80',
    accent: '#5DBCBF',
    badge: 'Tarifs CE',
    sections: ['Cocktail & soirée', 'Budget pro', 'Animation CE', 'Facturation TVA'],
  },
  {
    id: 'bapteme',
    href: '/catalogue',
    emoji: '🕊️',
    title: 'Baptême & communion',
    desc: 'Décoration douce, animations enfants, animations gourmandes pour célébrer ensemble.',
    image: 'https://images.unsplash.com/photo-1768900318188-79f4291ad4a8?w=600&q=80',
    accent: '#F4A7B2',
    badge: 'Intime & élégant',
    sections: ['Décoration douce', 'Menu gourmand', 'Animations enfants', 'Ambiance'],
  },
  {
    id: 'garden-party',
    href: '/guide/bapteme',
    emoji: '🌿',
    title: 'Garden party',
    desc: 'Ambiance estivale, brasero, jeux en bois, animations outdoor et bar à bonbons.',
    image: 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=600&q=80',
    accent: '#1F9D7A',
    badge: 'Outdoor',
    sections: ['Jeux outdoor', 'Bar convivial', 'Brasero & grill', 'Déco naturelle'],
  },
];

const FEATURES = [
  { icon: CheckSquare, title: 'Checklist interactive', desc: 'Ne rien oublier grâce à nos listes détaillées par type d\'événement', color: '#5DBCBF' },
  { icon: Wallet, title: 'Tableau de budget', desc: 'Suivez votre budget en temps réel : prévu, réel, payé, restant', color: '#D4AF37' },
  { icon: Users, title: 'Gestion des invités', desc: 'RSVP, allergies, tablage, relances automatiques', color: '#F4A7B2' },
  { icon: Calendar, title: 'Planning Jour J', desc: 'Déroulé heure par heure, responsables, statuts', color: '#1F9D7A' },
  { icon: BookOpen, title: 'Guide prestataires', desc: 'Photographes, DJ, traiteurs recommandés par Show Événement', color: '#C58B19' },
  { icon: Star, title: 'Inspirations 2026', desc: 'Thèmes tendance, palettes couleurs, idées décoration', color: '#B94B5C' },
];

export default function GuidesPage() {
  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      {/* HERO */}
      <section className="section-py" style={{ background: 'linear-gradient(135deg, #0B1E20 0%, #1a3a3c 100%)', borderBottom: '3px solid #D4AF37' }}>
        <div className="container-ose text-center">
          <span className="inline-block px-4 py-2 rounded-full text-xs font-bold mb-4"
            style={{ background: 'rgba(212,175,55,0.2)', color: '#D4AF37' }}>
            ✨ Organisateur événementiel intelligent
          </span>
          <h1 className="font-display text-3xl sm:text-5xl font-bold mb-4"
            style={{ color: '#FFFFFF', letterSpacing: '-0.03em', lineHeight: '1.1' }}>
            Guides d'organisation<br />
            <span style={{ color: '#D4AF37' }}>par type d'événement</span>
          </h1>
          <p className="text-sm sm:text-base mb-8 max-w-2xl mx-auto"
            style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.75' }}>
            Checklists, budget, planning, prestataires, thèmes 2026… Tout ce dont vous avez besoin
            pour organiser un événement parfait, étape par étape.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-2">
            {[['✅', 'Accompagnement complet'], ['📋', 'Checklists détaillées'], ['💰', 'Suivi budget'], ['🎯', 'Tendances 2026']].map(([icon, label]) => (
              <div key={label} className="flex items-center gap-2 text-sm font-medium" style={{ color: 'rgba(255,255,255,0.85)' }}>
                <span>{icon}</span> {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES STRIP */}
      <section style={{ background: '#FEFAF5', borderBottom: '1px solid var(--border)' }}>
        <div className="container-ose">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-0 divide-x divide-y sm:divide-y-0" style={{ borderTop: '1px solid var(--border)' }}>
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="p-4 text-center">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center mx-auto mb-2"
                    style={{ background: `${f.color}18` }}>
                    <Icon size={16} style={{ color: f.color }} />
                  </div>
                  <p className="text-xs font-bold mb-0.5" style={{ color: 'var(--ink)' }}>{f.title}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: '1.4' }}>{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GRILLE GUIDES */}
      <section className="section-py">
        <div className="container-ose">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              Choisissez votre type d'événement
            </h2>
            <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>
              Chaque guide est pensé spécifiquement pour les besoins de votre événement
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {GUIDES.map(g => (
              <Link key={g.id} to={g.href}
                className="card-premium overflow-hidden group block hover:shadow-xl transition-shadow duration-300">
                {/* Image */}
                <div className="relative overflow-hidden" style={{ height: '200px' }}>
                  <img src={g.image} alt={g.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(11,30,32,0.7) 0%, transparent 60%)' }} />
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold"
                    style={{ background: g.accent, color: '#0B1E20' }}>
                    {g.badge}
                  </span>
                  <div className="absolute bottom-3 left-3">
                    <p className="font-display text-xl font-bold" style={{ color: '#FFFFFF' }}>
                      {g.emoji} {g.title}
                    </p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <p className="text-sm mb-4" style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>
                    {g.desc}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {g.sections.map((s, i) => (
                      <span key={i} className="px-2 py-1 rounded-lg text-xs font-medium"
                        style={{ background: `${g.accent}18`, color: g.accent }}>
                        {s}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 text-sm font-semibold" style={{ color: g.accent }}>
                    Accéder au guide <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA COMPTE */}
      <section className="section-py" style={{ background: '#0B1E20' }}>
        <div className="container-ose text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-4" style={{ color: '#FFFFFF' }}>
            Organisez tout depuis votre espace personnel
          </h2>
          <p className="text-sm mb-8 max-w-xl mx-auto" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Créez un compte gratuit pour sauvegarder vos checklists, suivre votre budget, gérer vos invités et retrouver tous vos documents à tout moment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/connexion"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-sm"
              style={{ background: '#D4AF37', color: '#0B1E20' }}>
              Créer mon espace gratuit <ArrowRight size={16} />
            </Link>
            <Link to="/creer-evenement"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-sm"
              style={{ border: '1px solid rgba(255,255,255,0.25)', color: '#FFFFFF' }}>
              Composer mon événement
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
    </div>
  );
}
